import { Controller } from '@hotwired/stimulus'

const SAVE_FAILED_MESSAGE = 'The new order could not be saved. The previous order has been restored; please try again.'

// Lets the admin drag the periods without dates (Settings, Periods of Life) to
// change their order. A row only becomes draggable while its handle is pressed,
// so the rest of the row keeps normal text selection; the arrow keys on a focused
// handle move the row too. The new order goes out through a hidden form.
//
// Saves go out one at a time: moves made while one is in flight are sent once it
// finishes. The server answers with a Turbo Stream that redraws the table from
// the database (so periods another admin added or deleted show up), but a reply
// is only applied when nothing has moved since its save went out and no drag is
// in progress; otherwise the newer save that follows brings the redraw. A drag
// cancelled with Esc or released outside the list puts the row back, and a
// failed save restores the last saved order and tells the admin.
export default class PeriodOrderController extends Controller {
  static targets = ['row', 'form', 'inputs']

  // The table is replaced after a save, so remember which handle had focus and
  // give it back once the new table connects.
  static focusId = null

  connect () {
    this.dragged = null
    this.dropped = false
    this.saving = false
    this.savedOrder = this.#order()
    this.#restoreFocus()
  }

  arm (event) {
    event.currentTarget.closest('tr').draggable = true
  }

  disarm () {
    if (!this.dragged) this.rowTargets.forEach(row => { row.draggable = false })
  }

  keydown (event) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return

    event.preventDefault()
    const handle = event.currentTarget
    const rows = this.rowTargets
    const row = handle.closest('tr')
    const up = event.key === 'ArrowUp'
    const neighbour = rows[rows.indexOf(row) + (up ? -1 : 1)]
    if (!neighbour) return

    neighbour.parentElement.insertBefore(row, up ? neighbour : neighbour.nextSibling)
    // Moving the row drops focus; put it straight back so quick repeated presses keep working.
    handle.focus()
    this.#save()
  }

  dragStart (event) {
    const row = this.#rowFor(event.target)
    if (!row?.draggable) return

    this.dragged = row
    this.dropped = false
    this.dragStartOrder = this.#order()
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', row.dataset.id)
    row.classList.add('period-order__row--dragging')
  }

  // Moves the dragged row live, so the admin sees where it will land. Only the
  // dateless rows accept a drop, which keeps dated periods out of reach.
  dragOver (event) {
    if (!this.dragged) return

    const target = this.#rowFor(event.target)
    if (!target) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    if (target === this.dragged) return

    const box = target.getBoundingClientRect()
    const before = event.clientY < box.top + box.height / 2
    target.parentElement.insertBefore(this.dragged, before ? target : target.nextSibling)
  }

  // Only a drop on the list commits the move. preventDefault also stops some
  // browsers from opening the dropped text as a link.
  drop (event) {
    if (!this.dragged || !this.#rowFor(event.target)) return

    event.preventDefault()
    this.dropped = true
  }

  // dragend fires for every drag, including ones cancelled with Esc or released
  // outside the list; those put the row back where it started.
  dragEnd () {
    if (!this.dragged) return

    this.dragged.classList.remove('period-order__row--dragging')
    this.dragged.draggable = false
    this.dragged = null

    if (this.dropped) {
      this.#save()
    } else {
      this.#keepingFocus(() => this.#arrange(this.dragStartOrder))
    }
  }

  // Runs on the form before Turbo handles the reply. Error replies are never
  // rendered as a page (saveEnd undoes the move instead), and a redraw is skipped
  // while it would undo a move the admin made after the save went out.
  // preventDefault alone is not enough: Turbo renders stream replies from a
  // listener on document that ignores it, so the event must not get that far.
  saveResponse (event) {
    const stale = this.dragged || this.#order().join(',') !== this.sentOrder.join(',')

    if (!event.detail.fetchResponse.succeeded || stale) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      this.#rememberFocus()
    }
  }

  // Fires after every save, including ones that never got a reply (offline, timeout).
  saveEnd (event) {
    this.saving = false

    if (event.detail.success) {
      this.savedOrder = this.sentOrder
      this.#save()
    } else {
      this.#saveFailed()
    }
  }

  #saveFailed () {
    PeriodOrderController.focusId = null
    // A drag still in progress started from the order that just failed; cancelling
    // it must fall back to the saved order, not to that one.
    this.dragStartOrder = this.savedOrder
    this.#keepingFocus(() => this.#arrange(this.savedOrder))
    window.alert(SAVE_FAILED_MESSAGE)
  }

  #rowFor (element) {
    const row = element.closest?.('tr')
    return this.rowTargets.includes(row) ? row : null
  }

  #order () {
    return this.rowTargets.map(row => row.dataset.id)
  }

  // Puts the dateless rows in the given order, in the same place in the table.
  #arrange (ids) {
    const rows = this.rowTargets
    const byId = new Map(rows.map(row => [row.dataset.id, row]))
    const after = rows[rows.length - 1].nextElementSibling
    ids.forEach(id => byId.get(id)?.parentElement.insertBefore(byId.get(id), after))
  }

  // Re-inserting a row drops the focus inside it; give it back afterwards.
  #keepingFocus (callback) {
    const focused = document.activeElement
    callback()
    if (focused && focused !== document.activeElement && this.element.contains(focused)) focused.focus()
  }

  // Sends the order on screen unless it is already saved or a save is in flight
  // (saveEnd calls this again once that one is done).
  #save () {
    const order = this.#order()
    if (this.saving || order.join(',') === this.savedOrder.join(',')) return

    this.saving = true
    this.sentOrder = order
    const inputs = order.map(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'life_period_ids[]'
      input.value = id
      return input
    })

    this.inputsTarget.replaceChildren(...inputs)
    this.formTarget.requestSubmit()
  }

  #rememberFocus () {
    const handle = document.activeElement?.closest?.('.period-order__handle')
    if (handle && this.element.contains(handle)) PeriodOrderController.focusId = handle.closest('tr').dataset.id
  }

  #restoreFocus () {
    const id = PeriodOrderController.focusId
    if (!id) return

    PeriodOrderController.focusId = null
    this.rowTargets.find(row => row.dataset.id === id)?.querySelector('.period-order__handle')?.focus()
  }
}
